import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import { AsButton, AsNumber, AsText, AsDatePicker, RadioButton } from '@adserve/adserve-react-components';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import {
    panelTitleStyle,
    buttonStyle,
    datePickerInputStyle,
    datePickerButtonStyle,
    offAirLabelStyle,
    offAirInputStyle,
    radioButton,
    radioButtonText
} from './OffAirCostsStyles';
import OffAirDropdown from './OffAirDropdown/Dropdown';
import LogSelection from '../../../components/Campaign/Properties/LogSelection';
import { mdFormat } from './OffAirCostsUtils';
import { GetStartOfDay } from '../../../services/DateService';
import { GetActiveStations } from '../../../services/DataService';
import { GetAccountById, GetAllEffectiveVats, GetOffairType } from '../../../services/CampaignsDataService';

const CreateOffAirCost = ({
    readOnly = false,
    currentOffairCost,
    offairTypes,
    handleChange,
    handleChangeByName,
    handleSave,
    handleClear,
    campaign,
    billingAccountId,
    updateCurrentOffairCost,
    campaignStartDate,
    campaignEndDate,
    setInitialDates,
    currentRevenueType = {}
}) => {
    const { formatCurrency, dateFormat, formatNumber } = useContext(SystemSettingsContext);
    const [activeStations, setActiveStations] = useState([]);
    const [selectedStations, setSelectedStations] = useState([]);
    const [isOneTime, setIsOneTime] = useState(true);
    const todayDate = new Date();
    const startDate = new Date(campaignStartDate);
    const endDate = new Date(campaignEndDate);

    const offairTypesOptions = [{ id: window.emptyGuid, name: 'Select...' }, ...offairTypes];
    const clearAll = () => {
        setSelectedStations([]);
        setIsOneTime(true);
        handleClear();
    };
    const doSave = () => {
        setSelectedStations([]);
        handleSave();
    };
    const handleOffAirTypeChange = (name, value) => {
        let oatName = '';
        if (value !== window.emptyGuid) {
            oatName = offairTypes.filter((ot) => ot.id === value)[0].name;
        } else {
            value = window.emptyGuid;
        }
        handleChangeByName(name, value);
        handleChangeByName('offairTypeName', oatName);
    };

    function billingChange(e) {
        const value = e.target.value;
        handleChangeByName('billingDate', value);
    }

    function startChange(e) {
        const value = e.target.value;
        if (currentOffairCost.endDate && value > currentOffairCost.endDate) {
            handleChangeByName('startDate', value);
            handleChangeByName('endDate', value);
        } else {
            handleChangeByName('startDate', value);
        }
    }

    function endChange(e) {
        const value = e.target.value;
        if (currentOffairCost.startDate && value < currentOffairCost.startDate) {
            handleChangeByName('endDate', currentOffairCost.startDate);
            handleChangeByName('startDate', value);
        } else {
            handleChangeByName('endDate', value);
        }
    }

    const loadStations = async () => {
        GetActiveStations().then((data) => {
            setActiveStations(data);
        });
    };

    useEffect(() => {
        const getData = async () => {
            if (!currentOffairCost.offairTypeId || currentOffairCost.offairTypeId === window.emptyGuid) {
                return;
            }
            const rateData = await GetAccountById(billingAccountId);
            const vats = await GetAllEffectiveVats(currentOffairCost.endDate);
            const data = await GetOffairType(currentOffairCost.offairTypeId);
            const vatId = campaign?.vatId === rateData.vatId ? rateData.vatId : campaign?.vatId;
            const vatDate = vats.find((v) => v.vatId === vatId);
            
            const campaignComission = !campaign.barterAgencyAccountId ? campaign.commissionRate : campaign.barterCommissionRate;
            const finalCommision = campaignComission === rateData.commissionRate ? rateData.commissionRate : campaignComission;

            // set Commission
            if (data.defaultCommission === false) {
                updateCurrentOffairCost('commission', 0);
            } else {
                updateCurrentOffairCost('commission', finalCommision);
            }
            // set Vat
            if (data.defaultVat === false) {
                updateCurrentOffairCost('vat', 0);
            } else {
                updateCurrentOffairCost('vat', vatDate.rate);
            }
            // set Date
            if (data.revenueAttribution === false) {
                setIsOneTime(false);

                if(data.allocatedType === 2) {
                    handleChangeByName('proDataDays', true);
                    handleChangeByName('proRateMonthly', false);
                } 
                
                if (data.allocatedType === 1) {
                    handleChangeByName('proRateMonthly', true);
                    handleChangeByName('proDataDays', false);
                }
            } else {
                setIsOneTime(true);
                setInitialDates();
            }
        };

        getData();
    }, [currentOffairCost.offairTypeId]);

    useEffect(() => {
        if (currentOffairCost.stationId !== null && currentOffairCost.stationId !== window.emptyGuid) {
            setSelectedStations(activeStations.filter((n) => n.id === currentOffairCost.stationId));
            currentOffairCost.stationIds = [currentOffairCost.stationId];
            handleChangeByName('stationIds', currentOffairCost.stationIds);
        }
    }, [currentOffairCost.stationId]);

    useEffect(() => {
        setInitialDates();
    }, [campaignStartDate, campaignEndDate]);

    useEffect(() => {
        loadStations();
    }, []);

    const handleApplyStationSelection = (selectedValues) => {
        const selectedStationIds = selectedValues.map((x) => x.id);

        currentOffairCost.stationIds = selectedStationIds;
        setSelectedStations(selectedValues);

        handleChangeByName('stationIds', selectedStationIds);
    };

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: `230px auto 230px repeat(${
                    7 + Number(campaign.billingTermName === 'Monthly')
                }, max-content)`,
                gap: '10px 20px',
                padding: 20,
                boxSizing: 'border-box',
                width: '100%'
            }}
        >
            <div style={{ ...panelTitleStyle, gridColumn: '1 / -2' }}>
                {currentOffairCost.id === window.emptyGuid ? 'Create ' : 'Update '}
                {window.translate('Off-Air Cost')}
            </div>

            <OffAirDropdown
                id='offair-type'
                name='offairTypeId'
                title={window.translate('Off-Air Type *')}
                values={offairTypesOptions}
                value={currentOffairCost.offairTypeId}
                onChange={handleOffAirTypeChange}
                style={{ width: 229, zIndex: 100 }}
                readOnly={readOnly}
            />

            <div>
                <AsText
                    id='offaircost-description'
                    name='description'
                    title={window.translate('Description')}
                    labelStyle={offAirLabelStyle}
                    inputStyle={{ ...offAirInputStyle, width: '100%' }}
                    value={currentOffairCost.description}
                    onChange={handleChange}
                    disabled={readOnly}
                    autoComplete={false}
                    maxLength={120}
                />
            </div>

            <LogSelection
                name='networkSelection'
                title={window.translate('Stations *')}
                height={400}
                maxVisibleLength={557}
                inputStyle={{ marginTop: 4, height: 40, width: 'auto' }}
                stations={activeStations}
                selectedStations={selectedStations}
                onApply={handleApplyStationSelection}
                readOnly={readOnly || currentOffairCost.id !== window.emptyGuid}
            />

            <div>
                <AsNumber
                    id='price'
                    name='price'
                    title={window.translate('Billing Price *')}
                    labelStyle={offAirLabelStyle}
                    inputStyle={{ ...offAirInputStyle, width: 100 }}
                    value={currentOffairCost.price}
                    onChange={handleChange}
                    autoComplete={'off'}
                    disabled={readOnly}
                    allowFloat={true}
                    disallowNegatives={true}
                    formatCurrency={formatCurrency}
                />
            </div>
            <div>
                <AsNumber
                    id='commission'
                    name='commission'
                    title={window.translate('Commission')}
                    labelStyle={offAirLabelStyle}
                    inputStyle={{ ...offAirInputStyle, width: 66 }}
                    value={currentOffairCost.commission}
                    onChange={handleChange}
                    autoComplete={'off'}
                    disabled={true}
                    allowFloat={true}
                    disallowNegatives={true}
                    formatCurrency={formatNumber}
                />
            </div>
            <div>
                <AsNumber
                    id='vat'
                    name='vat'
                    title={window.translate('Vat')}
                    labelStyle={offAirLabelStyle}
                    inputStyle={{ ...offAirInputStyle, width: 66 }}
                    value={currentOffairCost.vat}
                    onChange={handleChange}
                    autoComplete={'off'}
                    disabled={true}
                    allowFloat={true}
                    disallowNegatives={true}
                    formatCurrency={formatNumber}
                />
            </div>
            <div>
                <label style={offAirLabelStyle}>{window.translate('Start date')}</label>
                <AsDatePicker
                    id='startDatePicker'
                    inputStyle={{
                        ...datePickerInputStyle,
                        width: 125,
                        color: currentOffairCost.startDate ? datePickerInputStyle.color : '#8A8A8A'
                    }}
                    buttonStyle={datePickerButtonStyle}
                    currentDate={
                        currentOffairCost.startDate
                            ? moment(currentOffairCost?.startDate, mdFormat)?.utc(true)?.startOf('day')?.toISOString()
                            : window.translate('Select...')
                    }
                    style={{ flexWrap: 'nowrap' }}
                    onChange={startChange}
                    dateFormat={dateFormat}
                    useStrings={true}
                    disabled={readOnly || isOneTime}
                    minDate={currentRevenueType?.name === 'Digital' ? moment(new Date()).startOf('month') : moment(new Date())}
                />
            </div>
            <div>
                <label style={offAirLabelStyle}>{window.translate('End date')}</label>
                <AsDatePicker
                    id='endDatePicker'
                    inputStyle={{
                        ...datePickerInputStyle,
                        width: 125,
                        color: currentOffairCost.endDate ? datePickerInputStyle.color : '#8A8A8A'
                    }}
                    buttonStyle={datePickerButtonStyle}
                    currentDate={
                        currentOffairCost.endDate
                            ? moment(currentOffairCost?.endDate, mdFormat)?.utc(true).startOf('day')?.toISOString()
                            : 'Select...'
                    }
                    onChange={endChange}
                    dateFormat={dateFormat}
                    useStrings={true}
                    disabled={readOnly || isOneTime}
                    style={{ flexWrap: 'nowrap' }}
                    minDate={
                        currentOffairCost.startDate
                            ? moment(currentOffairCost.startDate).startOf('day') < new Date()
                                ? moment(new Date()).add('days', -1)
                                : moment(currentOffairCost.startDate).startOf('day')
                            : moment(new Date()).add('days', -1)
                    }
                />
            </div>

            {isOneTime && (
                <div>
                    <label style={offAirLabelStyle}>{window.translate('Invoice Date *')}</label>
                    <AsDatePicker
                        id='billingDatePicker'
                        inputStyle={{
                            ...datePickerInputStyle,
                            width: 125,
                            color: currentOffairCost.billingDate ? datePickerInputStyle.color : '#8A8A8A'
                        }}
                        buttonStyle={datePickerButtonStyle}
                        currentDate={
                            currentOffairCost.billingDate
                                ? moment(currentOffairCost?.billingDate, mdFormat)?.utc(true)?.startOf('day')?.toISOString()
                                : 'Select...'
                        }
                        onChange={billingChange}
                        dateFormat={dateFormat}
                        useStrings={true}
                        style={{ flexWrap: 'nowrap' }}
                        disabled={readOnly}
                        minDate={GetStartOfDay(new Date())}
                    />
                </div>
            )}
            <div
                style={{
                    gridColumn: '-2 / -1',
                    gridRow: '-1 / span 2',
                    flexDirection: 'column',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <AsButton
                    style={buttonStyle}
                    handleClick={doSave}
                    disabled={
                        currentOffairCost.offairTypeId === window.emptyGuid ||
                        !currentOffairCost.price ||
                        !(
                            currentOffairCost.billingDate ||
                            (currentOffairCost.startDate && currentOffairCost.endDate)
                        ) ||
                        currentOffairCost.stationIds?.length === 0 ||
                        !currentOffairCost.stationIds
                    }
                >
                    {currentOffairCost.id === window.emptyGuid ? window.translate('Add') : window.translate('Update')}
                </AsButton>
                <AsButton
                    style={buttonStyle}
                    useSecondaryStyle={{
                        backgroundColor: '#FAA800'
                    }}
                    handleClick={clearAll}
                    disabled={readOnly}
                >
                    {window.translate('Clear')}
                </AsButton>
            </div>
        </div>
    );
};

export default CreateOffAirCost;
